import * as React from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableRowNode,
  getDOMCellFromTarget,
  getTableElement,
  TableNode,
  type TableDOMCell,
  type TableMapType,
} from '@lexical/table';
import { calculateZoomLevel, isHTMLElement, mergeRegister } from '@lexical/utils';
import {
  $getNearestNodeFromDOMNode,
  SKIP_SCROLL_INTO_VIEW_TAG,
  type LexicalEditor,
} from 'lexical';
import { createPortal } from 'react-dom';
import styles from '../lexical-editor.module.css';

type PointerDraggingDirection = 'bottom' | 'right';
type PointerPosition = {
  x: number;
  y: number;
};

const ACTIVE_RESIZER_COLOR = '#76b6ff';
const MIN_COLUMN_WIDTH = 92;
const MIN_ROW_HEIGHT = 33;
const RESIZER_ZONE_SIZE = 16;

function getCellColumnIndex(
  tableCellNode: ReturnType<typeof $getNearestNodeFromDOMNode>,
  tableMap: TableMapType
) {
  if (!$isTableCellNode(tableCellNode)) {
    return undefined;
  }

  for (let rowIndex = 0; rowIndex < tableMap.length; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < tableMap[rowIndex].length; columnIndex += 1) {
      if (tableMap[rowIndex][columnIndex].cell === tableCellNode) {
        return columnIndex;
      }
    }
  }

  return undefined;
}

function getCellNodeHeight(
  editor: LexicalEditor,
  cellNode: ReturnType<typeof $getNearestNodeFromDOMNode>
) {
  if (!$isTableCellNode(cellNode)) {
    return undefined;
  }

  const element = editor.getElementByKey(cellNode.getKey());
  return element?.clientHeight;
}

function TableCellResizer({
  editor,
}: {
  editor: LexicalEditor;
}) {
  const [activeCell, setActiveCell] = React.useState<TableDOMCell | null>(null);
  const [draggingDirection, setDraggingDirection] =
    React.useState<PointerDraggingDirection | null>(null);
  const [hasTable, setHasTable] = React.useState(false);
  const [hoveredDirection, setHoveredDirection] =
    React.useState<PointerDraggingDirection | null>(null);
  const [pointerCurrentPosition, setPointerCurrentPosition] =
    React.useState<PointerPosition | null>(null);
  const pointerStartPositionRef = React.useRef<PointerPosition | null>(null);
  const pointerStartSizeRef = React.useRef<number | null>(null);
  const resizerRef = React.useRef<React.ComponentRef<'div'> | null>(null);
  const tableRectRef = React.useRef<
    ReturnType<TableDOMCell['elem']['getBoundingClientRect']> | null
  >(null);
  const targetRef = React.useRef<unknown>(null);

  const resetState = React.useCallback(() => {
    setActiveCell(null);
    setDraggingDirection(null);
    setHoveredDirection(null);
    setPointerCurrentPosition(null);
    pointerStartPositionRef.current = null;
    pointerStartSizeRef.current = null;
    tableRectRef.current = null;
    targetRef.current = null;
  }, []);

  React.useEffect(() => {
    const tableKeys = new Set<string>();

    return mergeRegister(
      editor.registerMutationListener(TableNode, (nodeMutations) => {
        for (const [nodeKey, mutation] of nodeMutations) {
          if (mutation === 'destroyed') {
            tableKeys.delete(nodeKey);
          } else {
            tableKeys.add(nodeKey);
          }
        }

        setHasTable(tableKeys.size > 0);
      }),
      editor.registerNodeTransform(TableNode, (tableNode) => {
        if (tableNode.getColWidths()) {
          return tableNode;
        }

        tableNode.setColWidths(
          Array(tableNode.getColumnCount()).fill(MIN_COLUMN_WIDTH)
        );

        return tableNode;
      })
    );
  }, [editor]);

  const updateColumnWidth = React.useCallback(
    (widthChange: number) => {
      if (!activeCell) {
        return;
      }

      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);

          if (!$isTableCellNode(tableCellNode)) {
            return;
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
          const columnIndex = getCellColumnIndex(tableCellNode, tableMap);
          const currentWidths = tableNode.getColWidths();

          if (columnIndex === undefined || !currentWidths) {
            return;
          }

          const nextWidths = [...currentWidths];
          const currentWidth = nextWidths[columnIndex];

          if (currentWidth === undefined) {
            return;
          }

          nextWidths[columnIndex] = Math.max(
            currentWidth + widthChange,
            MIN_COLUMN_WIDTH
          );
          tableNode.setColWidths(nextWidths);
        },
        {
          tag: SKIP_SCROLL_INTO_VIEW_TAG,
        }
      );
    },
    [activeCell, editor]
  );

  const updateRowHeight = React.useCallback(
    (heightChange: number) => {
      if (!activeCell) {
        return;
      }

      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);

          if (!$isTableCellNode(tableCellNode)) {
            return;
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const baseRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
          const tableRows = tableNode.getChildren();
          const targetRowIndex =
            tableCellNode.getColSpan() === tableNode.getColumnCount()
              ? baseRowIndex
              : baseRowIndex + tableCellNode.getRowSpan() - 1;
          const tableRow = tableRows[targetRowIndex];

          if (!$isTableRowNode(tableRow)) {
            return;
          }

          let currentHeight = tableRow.getHeight();

          if (currentHeight === undefined) {
            currentHeight = Math.min(
              ...tableRow
                .getChildren()
                .map((cell) => getCellNodeHeight(editor, cell) ?? Infinity)
            );
          }

          tableRow.setHeight(
            Math.max(currentHeight + heightChange, MIN_ROW_HEIGHT)
          );
        },
        {
          tag: SKIP_SCROLL_INTO_VIEW_TAG,
        }
      );
    },
    [activeCell, editor]
  );

  const toggleResize = React.useCallback(
    (direction: PointerDraggingDirection) => (event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!activeCell) {
        return;
      }

      pointerStartPositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      pointerStartSizeRef.current =
        direction === 'bottom'
          ? activeCell.elem.getBoundingClientRect().height
          : activeCell.elem.getBoundingClientRect().width;
      setPointerCurrentPosition(pointerStartPositionRef.current);
      setDraggingDirection(direction);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [activeCell]
  );

  React.useEffect(() => {
    if (!hasTable) {
      return;
    }

    const onPointerMove = (event: globalThis.PointerEvent) => {
      const target = event.target;

      if (!isHTMLElement(target)) {
        return;
      }

      if (draggingDirection) {
        event.preventDefault();
        event.stopPropagation();
        setPointerCurrentPosition({
          x: event.clientX,
          y: event.clientY,
        });
        return;
      }

      if (resizerRef.current?.contains(target)) {
        return;
      }

      if (targetRef.current === target) {
        return;
      }

      targetRef.current = target;
      const cell = getDOMCellFromTarget(target);

      if (!cell) {
        resetState();
        return;
      }

      if (activeCell === cell) {
        return;
      }

      editor.getEditorState().read(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(cell.elem);

          if (!$isTableCellNode(tableCellNode)) {
            return;
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const tableElement = getTableElement(
            tableNode,
            editor.getElementByKey(tableNode.getKey())
          );

          if (!tableElement) {
            return;
          }

          tableRectRef.current = tableElement.getBoundingClientRect();
          setActiveCell(cell);
        },
        {
          editor,
        }
      );
    };

    const onPointerDown = (event: globalThis.PointerEvent) => {
      if (event.pointerType === 'touch') {
        onPointerMove(event);
      }
    };

    const currentResizerRef = resizerRef.current;
    currentResizerRef?.addEventListener('pointermove', onPointerMove, {
      capture: true,
    });

    const removeRootListener = editor.registerRootListener(
      (rootElement, previousRootElement) => {
        previousRootElement?.removeEventListener('pointermove', onPointerMove);
        previousRootElement?.removeEventListener('pointerdown', onPointerDown);
        rootElement?.addEventListener('pointermove', onPointerMove);
        rootElement?.addEventListener('pointerdown', onPointerDown);
      }
    );

    return () => {
      removeRootListener();
      currentResizerRef?.removeEventListener('pointermove', onPointerMove, true);
    };
  }, [activeCell, draggingDirection, editor, hasTable, resetState]);

  React.useEffect(() => {
    if (!draggingDirection) {
      return;
    }

    const onPointerMove = (event: globalThis.PointerEvent) => {
      event.preventDefault();
      setPointerCurrentPosition({
        x: event.clientX,
        y: event.clientY,
      });
    };

    const finishDrag = (event: globalThis.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const pointerStartPosition = pointerStartPositionRef.current;
      const pointerStartSize = pointerStartSizeRef.current;

      if (!pointerStartPosition || pointerStartSize === null) {
        resetState();
        return;
      }

      const zoomLevel = calculateZoomLevel(activeCell?.elem ?? null);

      if (draggingDirection === 'bottom') {
        const nextHeight =
          pointerStartSize + (event.clientY - pointerStartPosition.y) / zoomLevel;
        updateRowHeight(nextHeight - pointerStartSize);
      } else {
        const nextWidth =
          pointerStartSize + (event.clientX - pointerStartPosition.x) / zoomLevel;
        updateColumnWidth(nextWidth - pointerStartSize);
      }

      resetState();
    };

    const cancelDrag = () => {
      resetState();
    };

    globalThis.window.addEventListener('pointermove', onPointerMove, {
      passive: false,
    });
    globalThis.window.addEventListener('pointerup', finishDrag, {
      passive: false,
    });
    globalThis.window.addEventListener('pointercancel', finishDrag, {
      passive: false,
    });
    globalThis.window.addEventListener('blur', cancelDrag);

    return () => {
      globalThis.window.removeEventListener('pointermove', onPointerMove);
      globalThis.window.removeEventListener('pointerup', finishDrag);
      globalThis.window.removeEventListener('pointercancel', finishDrag);
      globalThis.window.removeEventListener('blur', cancelDrag);
    };
  }, [activeCell, draggingDirection, resetState, updateColumnWidth, updateRowHeight]);

  const resizerStyles = React.useMemo(() => {
    if (!activeCell) {
      return {
        bottom: null,
        right: null,
      } as const;
    }

    const { height, left, top, width } = activeCell.elem.getBoundingClientRect();
    const tableRect = tableRectRef.current;

    const nextStyles: {
      bottom: React.CSSProperties;
      right: React.CSSProperties;
    } = {
      bottom: {
        height: `${RESIZER_ZONE_SIZE}px`,
        left: `${globalThis.window.scrollX + left}px`,
        top: `${globalThis.window.scrollY + top + height - RESIZER_ZONE_SIZE / 2}px`,
        width: `${width}px`,
      },
      right: {
        height: `${height}px`,
        left: `${globalThis.window.scrollX + left + width - RESIZER_ZONE_SIZE / 2}px`,
        top: `${globalThis.window.scrollY + top}px`,
        width: `${RESIZER_ZONE_SIZE}px`,
      },
    };

    if (draggingDirection && pointerCurrentPosition && tableRect) {
      if (draggingDirection === 'bottom') {
        nextStyles.bottom = {
          backgroundColor: ACTIVE_RESIZER_COLOR,
          height: '3px',
          left: `${globalThis.window.scrollX + tableRect.left}px`,
          top: `${globalThis.window.scrollY + pointerCurrentPosition.y}px`,
          width: `${tableRect.width}px`,
        };
      } else {
        nextStyles.right = {
          backgroundColor: ACTIVE_RESIZER_COLOR,
          height: `${tableRect.height}px`,
          left: `${globalThis.window.scrollX + pointerCurrentPosition.x}px`,
          top: `${globalThis.window.scrollY + tableRect.top}px`,
          width: '3px',
        };
      }
    }

    return nextStyles;
  }, [activeCell, draggingDirection, pointerCurrentPosition]);

  if (!activeCell) {
    return null;
  }

  return (
    <div ref={resizerRef}>
      <div
        className={styles.tableResizeHandle}
        data-active={draggingDirection === 'right' || hoveredDirection === 'right'}
        data-direction="right"
        onPointerDown={toggleResize('right')}
        onPointerEnter={() => {
          if (!draggingDirection) {
            setHoveredDirection('right');
          }
        }}
        onPointerLeave={() => {
          if (!draggingDirection) {
            setHoveredDirection(null);
          }
        }}
        style={resizerStyles.right ?? undefined}
      />
      <div
        className={styles.tableResizeHandle}
        data-active={draggingDirection === 'bottom' || hoveredDirection === 'bottom'}
        data-direction="bottom"
        onPointerDown={toggleResize('bottom')}
        onPointerEnter={() => {
          if (!draggingDirection) {
            setHoveredDirection('bottom');
          }
        }}
        onPointerLeave={() => {
          if (!draggingDirection) {
            setHoveredDirection(null);
          }
        }}
        style={resizerStyles.bottom ?? undefined}
      />
    </div>
  );
}

export function TableCellResizerPlugin() {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();

  if (!isEditable || typeof globalThis.document === 'undefined') {
    return null;
  }

  return createPortal(<TableCellResizer editor={editor} />, globalThis.document.body);
}
